// frontend/src/components/areas/AreasListMobile.tsx
// NEW: Mobile-first areas list component

import React from 'react';
import { 
  MapPin, 
  Eye, 
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ServiceArea } from '@/lib/api/areasService';
import { ActionsDropdown, createCommonActions } from '@/components/common/ui';

interface AreasListMobileProps {
  areas: ServiceArea[];
  pagination?: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
    has_more: boolean;
  };
  currentPage: number;
  onPageChange: (page: number) => void;
  onEditArea: (area: ServiceArea) => void;
  onToggleStatus: (area: ServiceArea) => void;
  isAdmin?: boolean;
}

const AreasListMobile: React.FC<AreasListMobileProps> = ({
  areas,
  pagination,
  currentPage,
  onPageChange,
  onEditArea,
  onToggleStatus,
  isAdmin = false
}) => {
  return (
    <div className="space-y-4">
      {/* Areas List */}
      <div className="space-y-3">
        {areas.map((area) => (
          <AreaCardMobile
            key={area.area_id}
            area={area}
            onEdit={onEditArea}
            onToggleStatus={onToggleStatus}
            isAdmin={isAdmin}
          />
        ))}
      </div>

      {/* Mobile Pagination */}
      {pagination && pagination.last_page > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          {/* Stats */}
          <div className="text-center mb-4">
            <p className="text-sm text-gray-600">
              Showing {pagination.from} to {pagination.to} of {pagination.total} areas
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>

            <div className="flex items-center gap-2">
              {/* Page numbers - show current and adjacent pages */}
              {currentPage > 2 && (
                <>
                  <button
                    onClick={() => onPageChange(1)}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                  >
                    1
                  </button>
                  {currentPage > 3 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                </>
              )}

              {currentPage > 1 && (
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  {currentPage - 1}
                </button>
              )}

              <button
                className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg font-medium"
              >
                {currentPage}
              </button>

              {currentPage < pagination.last_page && (
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                >
                  {currentPage + 1}
                </button>
              )}

              {currentPage < pagination.last_page - 1 && (
                <>
                  {currentPage < pagination.last_page - 2 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => onPageChange(pagination.last_page)}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                  >
                    {pagination.last_page}
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === pagination.last_page}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual area card component
const AreaCardMobile: React.FC<{
  area: ServiceArea;
  onEdit: (area: ServiceArea) => void;
  onToggleStatus: (area: ServiceArea) => void;
  isAdmin: boolean;
}> = ({ area, onEdit, onToggleStatus, isAdmin }) => {

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            area.is_active 
              ? 'bg-green-100 text-green-600' 
              : 'bg-gray-100 text-gray-400'
          }`}>
            <MapPin className="h-5 w-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 truncate">
              {area.city}, {area.state}
            </h3>
            <p className="text-sm text-gray-600">
              PIN: {area.pincode}
            </p>
          </div>
        </div>
        
        {/* Status Badge and Actions */}
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            area.is_active 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-800'
          }`}>
            {area.is_active ? 'Active' : 'Inactive'}
          </span>
          
          <ActionsDropdown
            size="sm"
            actions={[
              ...(isAdmin ? [
                createCommonActions.view(() => onEdit(area))
              ] : [
                createCommonActions.edit(() => onEdit(area)),
                createCommonActions.toggleStatus(
                  area.is_active,
                  () => onToggleStatus(area)
                )
              ])
            ]}
          />
        </div>
      </div>

      {/* Location Details */}
      <div className="space-y-2 mb-4">
        {area.district && (
          <p className="text-sm text-gray-600">
            <span className="font-medium">District:</span> {area.district}
          </p>
        )}
        
        {area.region && (
          <p className="text-sm text-gray-600">
            <span className="font-medium">Region:</span> {area.region}
          </p>
        )}
        
        <p className="text-sm text-gray-600">
          <span className="font-medium">Created:</span> {new Date(area.created_at).toLocaleDateString()}
        </p>
      </div>

      {/* Admin Info */}
      {isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-900 font-medium">Provider ID: {area.provider_id}</p>
          {area.updated_at && (
            <p className="text-sm text-blue-700">
              Updated: {new Date(area.updated_at).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Actions have been moved to the header dropdown */}

      {/* Removed absolute overlay - actions now only available through dropdown */}
    </div>
  );
};

export default AreasListMobile;